import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator, FileText } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import BasicInformationSection from './BasicInformationSection';
import ProjectParametersSection from './ProjectParametersSection';
import ConsultingSection from './ConsultingSection';
import TechnologySection from './TechnologySection';
import TrainingSection from './TrainingSection';
import ROISection from './ROISection';
import ContingencySection from './ContingencySection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета стоимости трансформации бизнес-процессов
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  costBreakdown,
  setCostBreakdown,
  transformationTypes,
  companySizes,
  industryTypes,
  consultingLevels,
  saveProject 
}) => {
  
  // Состояние для имени сохраняемого проекта
  const [saveProjectName, setSaveProjectName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Проверяем специальные условия и зависимости полей
      
      // Сбрасываем значения консалтинга, если он не требуется
      if (name === 'consultingRequired' && !checked) {
        newState.consultingLevel = 'standard';
        newState.consultingDuration = '6';
      }
      
      // Сбрасываем значения ПО, если оно не требуется
      if (name === 'softwareImplementation' && !checked) {
        newState.softwareType = 'custom';
        newState.softwareCost = '0';
      }
      
      // Сбрасываем значения оборудования, если оно не требуется
      if (name === 'hardwareUpgrades' && !checked) {
        newState.hardwareCost = '0';
      }
      
      // Сбрасываем значения обучения, если оно не требуется
      if (name === 'trainingRequired' && !checked) {
        newState.trainingIntensity = 'medium';
        newState.employeesToTrain = '0';
        newState.trainingCostPerEmployee = '0';
      }
      
      // Сбрасываем значения ROI, если он не требуется
      if (name === 'includeROICalculation' && !checked) {
        newState.expectedEfficiencyGain = '0';
        newState.expectedRevenueIncrease = '0';
        newState.currentAnnualRevenue = '0';
        newState.currentOperationalCosts = '0';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      companySize, industryType, transformationType, projectDuration,
      internalTeamSize, averageSalary, allocatedTimePercentage,
      consultingRequired, consultingLevel, consultingDuration,
      softwareImplementation, softwareType, softwareCost,
      hardwareUpgrades, hardwareCost,
      trainingRequired, trainingIntensity, employeesToTrain, trainingCostPerEmployee,
      changeSeverity, organizationalReadiness,
      includeContingency, contingencyPercentage,
      includeROICalculation, expectedEfficiencyGain, expectedRevenueIncrease, 
      currentAnnualRevenue, currentOperationalCosts
    } = formData;

    // Базовая валидация
    if (!companySize) newErrors.companySize = 'Выберите размер компании';
    if (!industryType) newErrors.industryType = 'Выберите отрасль';
    if (!transformationType) newErrors.transformationType = 'Выберите тип трансформации';

    // Валидация продолжительности проекта
    const numProjectDuration = parseIntOrDefault(projectDuration, -1);
    if (!isPositiveNumber(numProjectDuration)) newErrors.projectDuration = 'Продолжительность должна быть положительным числом';
    if (numProjectDuration > 60) newErrors.projectDuration = 'Максимальная продолжительность 60 месяцев';

    // Валидация внутренней команды
    const numInternalTeamSize = parseIntOrDefault(internalTeamSize, -1);
    if (!isInteger(internalTeamSize) || numInternalTeamSize < 0) {
      newErrors.internalTeamSize = 'Введите корректное количество сотрудников';
    }

    const numAverageSalary = parseFloatOrDefault(averageSalary, -1);
    if (!isPositiveNumber(numAverageSalary)) newErrors.averageSalary = 'Зарплата должна быть положительным числом';

    const numAllocatedTimePercentage = parseFloatOrDefault(allocatedTimePercentage, -1);
    if (numAllocatedTimePercentage <= 0 || numAllocatedTimePercentage > 100) {
      newErrors.allocatedTimePercentage = 'Процент должен быть от 1 до 100';
    }

    // Валидация консалтинга
    if (consultingRequired) {
      if (!consultingLevel) newErrors.consultingLevel = 'Выберите уровень консалтинга';
      
      const numConsultingDuration = parseIntOrDefault(consultingDuration, -1);
      if (!isPositiveNumber(numConsultingDuration)) {
        newErrors.consultingDuration = 'Продолжительность должна быть положительным числом';
      }
      if (numConsultingDuration > numProjectDuration) {
        newErrors.consultingDuration = 'Консалтинг не может быть дольше проекта';
      }
    }

    // Валидация программного обеспечения
    if (softwareImplementation) {
      if (!softwareType) newErrors.softwareType = 'Выберите тип ПО';
      
      const numSoftwareCost = parseFloatOrDefault(softwareCost, -1);
      if (!isPositiveNumber(numSoftwareCost)) {
        newErrors.softwareCost = 'Стоимость должна быть положительным числом';
      }
    }

    // Валидация оборудования
    if (hardwareUpgrades) {
      const numHardwareCost = parseFloatOrDefault(hardwareCost, -1);
      if (!isPositiveNumber(numHardwareCost)) {
        newErrors.hardwareCost = 'Стоимость должна быть положительным числом';
      }
    }

    // Валидация обучения
    if (trainingRequired) {
      if (!trainingIntensity) newErrors.trainingIntensity = 'Выберите интенсивность обучения';
      
      const numEmployeesToTrain = parseIntOrDefault(employeesToTrain, -1);
      if (!isPositiveNumber(numEmployeesToTrain)) {
        newErrors.employeesToTrain = 'Количество должно быть положительным числом';
      }
      
      const numTrainingCostPerEmployee = parseFloatOrDefault(trainingCostPerEmployee, -1);
      if (!isPositiveNumber(numTrainingCostPerEmployee)) {
        newErrors.trainingCostPerEmployee = 'Стоимость должна быть положительным числом';
      }
    }

    // Валидация факторов изменений
    if (!changeSeverity) newErrors.changeSeverity = 'Выберите уровень изменений';
    if (!organizationalReadiness) newErrors.organizationalReadiness = 'Выберите уровень готовности';

    // Валидация резерва на непредвиденные расходы
    if (includeContingency) {
      const numContingencyPercentage = parseFloatOrDefault(contingencyPercentage, -1);
      if (numContingencyPercentage <= 0 || numContingencyPercentage > 100) {
        newErrors.contingencyPercentage = 'Процент должен быть от 1 до 100';
      }
    }

    // Валидация ROI
    if (includeROICalculation) {
      const numEfficiencyGain = parseFloatOrDefault(expectedEfficiencyGain, -1);
      if (numEfficiencyGain < 0 || numEfficiencyGain > 100) {
        newErrors.expectedEfficiencyGain = 'Процент должен быть от 0 до 100';
      }
      
      const numRevenueIncrease = parseFloatOrDefault(expectedRevenueIncrease, -1);
      if (numRevenueIncrease < 0 || numRevenueIncrease > 100) {
        newErrors.expectedRevenueIncrease = 'Процент должен быть от 0 до 100';
      }
      
      const numCurrentRevenue = parseFloatOrDefault(currentAnnualRevenue, -1);
      if (!isPositiveNumber(numCurrentRevenue)) {
        newErrors.currentAnnualRevenue = 'Выручка должна быть положительным числом';
      }
      
      const numCurrentOperationalCosts = parseFloatOrDefault(currentOperationalCosts, -1);
      if (!isPositiveNumber(numCurrentOperationalCosts)) {
        newErrors.currentOperationalCosts = 'Расходы должны быть положительным числом';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Расчет стоимости трансформации бизнес-процессов
  const calculateTransformationCost = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setCostBreakdown([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        
        // Базовые данные проекта
        const companySize = formData.companySize;
        const industryType = formData.industryType;
        const transformationType = formData.transformationType;
        const projectDurationMonths = parseIntOrDefault(formData.projectDuration, 12);
        
        // Коэффициенты на основе входных данных
        const companySizeFactors = {
          small: 0.7,
          medium: 1.0,
          large: 1.5,
          enterprise: 2.5
        };
        
        const transformationTypeFactors = {
          comprehensive: 1.5,
          digital: 1.3,
          lean: 0.9,
          agile: 1.0,
          automation: 1.2
        };
        
        // Находим коэффициент сложности для выбранной отрасли
        const industryFactor = industryTypes.find(i => i.id === industryType)?.complexityFactor || 1.0;
        const companySizeFactor = companySizeFactors[companySize] || 1.0;
        const transformationTypeFactor = transformationTypeFactors[transformationType] || 1.0;
        
        // --- Расчет стоимости персонала ---
        const internalTeamSize = parseIntOrDefault(formData.internalTeamSize, 0);
        const averageMonthlySalary = parseFloatOrDefault(formData.averageSalary, 0);
        const allocatedTimePercentage = parseFloatOrDefault(formData.allocatedTimePercentage, 0) / 100;
        
        const internalTeamCost = internalTeamSize * averageMonthlySalary * allocatedTimePercentage * projectDurationMonths;
        
        // --- Расчет стоимости консалтинга ---
        let consultingCost = 0;
        
        if (formData.consultingRequired) {
          const consultingLevel = formData.consultingLevel;
          const consultingDurationMonths = parseIntOrDefault(formData.consultingDuration, 0);
          
          // Находим коэффициент для выбранного уровня консалтинга
          const consultingLevelFactor = consultingLevels.find(c => c.id === consultingLevel)?.costFactor || 1.0;
          
          // Базовая месячная ставка консалтинга в зависимости от размера компании
          const baseConsultingRate = {
            small: 300000,
            medium: 600000,
            large: 1200000,
            enterprise: 2000000
          }[companySize] || 600000;
          
          consultingCost = baseConsultingRate * consultingLevelFactor * consultingDurationMonths;
        }
        
        // --- Расчет стоимости технологий ---
        let softwareCost = 0;
        let hardwareCost = 0;
        
        if (formData.softwareImplementation) {
          const softwareType = formData.softwareType;
          const declaredSoftwareCost = parseFloatOrDefault(formData.softwareCost, 0);
          
          // Коэффициенты для разных типов ПО
          const softwareFactors = {
            custom: 1.5,   // Кастомное ПО дороже из-за разработки
            'off-the-shelf': 1.0, // Готовое решение
            hybrid: 1.2    // Гибридное решение
          };
          
          const softwareFactor = softwareFactors[softwareType] || 1.0;
          
          // Если пользователь указал стоимость - используем её,
          // иначе рассчитываем на основе размера компании и коэффициентов
          if (declaredSoftwareCost > 0) {
            softwareCost = declaredSoftwareCost * softwareFactor;
          } else {
            // Базовая стоимость ПО в зависимости от размера компании
            const baseSoftwareCost = {
              small: 500000,
              medium: 1500000,
              large: 5000000,
              enterprise: 15000000
            }[companySize] || 1500000;
            
            softwareCost = baseSoftwareCost * softwareFactor * industryFactor;
          }
        }
        
        if (formData.hardwareUpgrades) {
          hardwareCost = parseFloatOrDefault(formData.hardwareCost, 0);
        }
        
        // --- Расчет стоимости обучения ---
        let trainingCost = 0;
        
        if (formData.trainingRequired) {
          const trainingIntensity = formData.trainingIntensity;
          const employeesToTrain = parseIntOrDefault(formData.employeesToTrain, 0);
          const trainingCostPerEmployee = parseFloatOrDefault(formData.trainingCostPerEmployee, 0);
          
          // Коэффициенты для разных интенсивностей обучения
          const trainingIntensityFactors = {
            low: 0.7,
            medium: 1.0,
            high: 1.5
          };
          
          const trainingIntensityFactor = trainingIntensityFactors[trainingIntensity] || 1.0;
          
          // Если пользователь указал стоимость - используем её,
          // иначе используем стандартную стоимость
          if (trainingCostPerEmployee > 0) {
            trainingCost = employeesToTrain * trainingCostPerEmployee * trainingIntensityFactor;
          } else {
            const standardTrainingCost = 25000; // Стандартная стоимость обучения на одного сотрудника
            trainingCost = employeesToTrain * standardTrainingCost * trainingIntensityFactor;
          }
        }
        
        // --- Расчет влияния сложности изменений и готовности организации ---
        const changeSeverityFactors = {
          minor: 0.8,
          moderate: 1.0,
          significant: 1.3,
          radical: 1.8
        };
        
        const organizationalReadinessFactors = {
          unprepared: 1.5,
          'somewhat-prepared': 1.2,
          prepared: 1.0,
          'fully-prepared': 0.8
        };
        
        const changeSeverityFactor = changeSeverityFactors[formData.changeSeverity] || 1.0;
        const organizationalReadinessFactor = organizationalReadinessFactors[formData.organizationalReadiness] || 1.0;
        
        // --- Расчет резерва на непредвиденные расходы ---
        let contingencyCost = 0;
        
        // --- Расчет общей стоимости проекта ---
        // Базовая стоимость без непредвиденных расходов
        const baseCost = (internalTeamCost + consultingCost + softwareCost + hardwareCost + trainingCost) * 
                        changeSeverityFactor * organizationalReadinessFactor;
        
        // Добавляем резерв на непредвиденные расходы, если необходимо
        if (formData.includeContingency) {
          const contingencyPercentage = parseFloatOrDefault(formData.contingencyPercentage, 0) / 100;
          contingencyCost = baseCost * contingencyPercentage;
        }
        
        // Итоговая стоимость проекта
        const totalCost = baseCost + contingencyCost;
        
        // --- Расчет ROI и срока окупаемости ---
        let roi = 0;
        let paybackPeriodMonths = 0;
        let annualBenefit = 0;
        
        if (formData.includeROICalculation) {
          const efficiencyGainPercentage = parseFloatOrDefault(formData.expectedEfficiencyGain, 0) / 100;
          const revenueIncreasePercentage = parseFloatOrDefault(formData.expectedRevenueIncrease, 0) / 100;
          const currentAnnualRevenue = parseFloatOrDefault(formData.currentAnnualRevenue, 0);
          const currentOperationalCosts = parseFloatOrDefault(formData.currentOperationalCosts, 0);
          
          // Годовая экономия от повышения эффективности
          const annualEfficiencySavings = currentOperationalCosts * efficiencyGainPercentage;
          
          // Годовое увеличение выручки
          const annualRevenueIncrease = currentAnnualRevenue * revenueIncreasePercentage;
          
          // Общая годовая выгода
          annualBenefit = annualEfficiencySavings + annualRevenueIncrease;
          
          // ROI за 3 года (%)
          roi = (annualBenefit * 3 - totalCost) / totalCost * 100;
          
          // Срок окупаемости в месяцах
          if (annualBenefit > 0) {
            paybackPeriodMonths = (totalCost / (annualBenefit / 12));
          } else {
            paybackPeriodMonths = Infinity; // Если нет выгоды, проект не окупается
          }
        }
        
        // --- Подготовка результатов ---
        const costBreakdownItems = [
          {
            category: "Внутренняя команда",
            cost: internalTeamCost,
            percentage: (internalTeamCost / totalCost) * 100
          },
          {
            category: "Консалтинговые услуги",
            cost: consultingCost,
            percentage: (consultingCost / totalCost) * 100
          },
          {
            category: "Программное обеспечение",
            cost: softwareCost,
            percentage: (softwareCost / totalCost) * 100
          },
          {
            category: "Оборудование и инфраструктура",
            cost: hardwareCost,
            percentage: (hardwareCost / totalCost) * 100
          },
          {
            category: "Обучение персонала",
            cost: trainingCost,
            percentage: (trainingCost / totalCost) * 100
          },
          {
            category: "Резерв на непредвиденные расходы",
            cost: contingencyCost,
            percentage: (contingencyCost / totalCost) * 100
          }
        ];
        
        // Сортируем категории затрат по убыванию
        costBreakdownItems.sort((a, b) => b.cost - a.cost);
        
        // Формируем финальный результат
        const calculationResults = {
          baseCost: baseCost,
          contingencyCost: contingencyCost,
          totalCost: totalCost,
          
          // Разбивка по категориям затрат
          costByCategory: {
            internalTeam: internalTeamCost,
            consulting: consultingCost,
            software: softwareCost,
            hardware: hardwareCost,
            training: trainingCost,
            contingency: contingencyCost
          },
          
          // Параметры проекта
          projectParameters: {
            industryFactor: industryFactor,
            companySizeFactor: companySizeFactor,
            transformationTypeFactor: transformationTypeFactor,
            changeSeverityFactor: changeSeverityFactor,
            organizationalReadinessFactor: organizationalReadinessFactor,
            projectDurationMonths: projectDurationMonths
          },
          
          // ROI и окупаемость
          roi: {
            annualBenefit: annualBenefit,
            roiPercent: roi,
            paybackPeriodMonths: paybackPeriodMonths
          },
          
          // Детализированная разбивка затрат
          costBreakdown: costBreakdownItems
        };
        
        setResults(calculationResults);
        setCostBreakdown(costBreakdownItems);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setCostBreakdown([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setCostBreakdown, setIsCalculating, industryTypes, consultingLevels]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      companySize: 'medium',
      industryType: 'manufacturing',
      transformationType: 'comprehensive',
      projectDuration: '12',
      internalTeamSize: '5',
      averageSalary: '60000',
      allocatedTimePercentage: '30',
      consultingRequired: true,
      consultingLevel: 'standard',
      consultingDuration: '6',
      softwareImplementation: true,
      softwareType: 'custom',
      softwareCost: '2000000',
      hardwareUpgrades: false,
      hardwareCost: '500000',
      trainingRequired: true,
      trainingIntensity: 'medium',
      employeesToTrain: '20',
      trainingCostPerEmployee: '30000',
      changeSeverity: 'moderate',
      organizationalReadiness: 'prepared',
      includeContingency: true,
      contingencyPercentage: '15',
      includeROICalculation: true,
      expectedEfficiencyGain: '20',
      expectedRevenueIncrease: '10',
      currentAnnualRevenue: '50000000',
      currentOperationalCosts: '30000000',
    });
    setResults(null);
    setCostBreakdown([]);
    setErrors({});
    setSaveProjectName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results || !costBreakdown || costBreakdown.length === 0) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Форматирование числа для CSV
    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    // Формируем основные данные проекта
    const basicInfo = [
      ['Калькулятор стоимости трансформации бизнес-процессов', ''],
      ['Дата расчета:', new Date().toLocaleDateString('ru-RU')],
      [''],
      ['ОСНОВНЫЕ ПАРАМЕТРЫ'],
      ['Размер компании:', companySizes.find(c => c.id === formData.companySize)?.name || formData.companySize],
      ['Отрасль:', industryTypes.find(i => i.id === formData.industryType)?.name || formData.industryType],
      ['Тип трансформации:', transformationTypes.find(t => t.id === formData.transformationType)?.name || formData.transformationType],
      ['Продолжительность проекта:', `${formData.projectDuration} мес.`],
      [''],
    ];

    // Формируем данные о затратах
    const costInfo = [
      ['ЗАТРАТЫ ПО КАТЕГОРИЯМ'],
      ['Категория', 'Стоимость (руб.)', 'Доля в общей стоимости (%)'],
      ...costBreakdown.map(item => [
        item.category,
        formatCsvNumber(item.cost),
        formatCsvNumber(item.percentage)
      ]),
      [''],
      ['ОБЩАЯ СТОИМОСТЬ ПРОЕКТА:', formatCsvNumber(results.totalCost), '100,00%'],
      ['']
    ];

    // Добавляем данные ROI, если они есть
    const roiInfo = formData.includeROICalculation ? [
      ['ПОКАЗАТЕЛИ ЭФФЕКТИВНОСТИ'],
      ['Годовая выгода (руб.):', formatCsvNumber(results.roi.annualBenefit)],
      ['ROI за 3 года (%):', formatCsvNumber(results.roi.roiPercent)],
      ['Срок окупаемости (мес.):', formatCsvNumber(results.roi.paybackPeriodMonths)],
      ['']
    ] : [];

    // Коэффициенты, использованные при расчете
    const factorsInfo = [
      ['ИСПОЛЬЗОВАННЫЕ КОЭФФИЦИЕНТЫ'],
      ['Фактор отрасли:', formatCsvNumber(results.projectParameters.industryFactor)],
      ['Фактор размера компании:', formatCsvNumber(results.projectParameters.companySizeFactor)],
      ['Фактор типа трансформации:', formatCsvNumber(results.projectParameters.transformationTypeFactor)],
      ['Фактор масштаба изменений:', formatCsvNumber(results.projectParameters.changeSeverityFactor)],
      ['Фактор готовности организации:', formatCsvNumber(results.projectParameters.organizationalReadinessFactor)]
    ];

    // Соединяем все данные в один массив
    const allRows = [
      ...basicInfo,
      ...costInfo,
      ...roiInfo,
      ...factorsInfo
    ];

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + allRows.map(row => row.join(';')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_стоимости_трансформации.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения проекта
  const handleSaveProject = () => {
    if (showSaveDialog) {
      const savedResult = saveProject(saveProjectName || null);
      if (savedResult) {
        alert(`Проект "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveProjectName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateTransformationCost();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart2 className="w-6 h-6 text-indigo-500" />
          Параметры расчета стоимости трансформации
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основная информация */}
        <BasicInformationSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          transformationTypes={transformationTypes}
          companySizes={companySizes}
          industryTypes={industryTypes}
        />

        {/* Параметры проекта */}
        <ProjectParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Консалтинг */}
        <ConsultingSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          consultingLevels={consultingLevels}
        />

        {/* Технологии */}
        <TechnologySection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Обучение */}
        <TrainingSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Непредвиденные расходы */}
        <ContingencySection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* ROI и выгоды */}
        <ROISection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения проекта */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveProjectName}
                onChange={(e) => setSaveProjectName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveProject}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать стоимость'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {costBreakdown.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать результаты (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveProject}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;